// @ts-check
// ── The new coach dashboard in the CRM ─────────────────────────────────────────
//
// docs/superpowers/plans/2026-10-14-dashboard-build.md: every card drawn from coach-dashboard.js, which works every
// number out on the server. It is THE dashboard (Clayton, 10/16: "there is no need to load anything from the old
// dashboard"): the old one, its markup and its on/off switch are gone. app.js renderDashboard calls
// showCoachDashboard, and calls it as soon as the dashboard view shows at boot, without waiting for the client list.
//
// ⚠️ A REFRESH DRAWS AT ONCE (10/16, "it flickers, some weird build, and then after a few seconds"). index.html
// carries the stylesheet and a placeholder of the page's shape from the first paint, and this tab's last answer
// (sessionStorage, for the signed-in coach only) is drawn straight away, then replaced by the server's.
//
// ⚠️ EVERY CLIENT CHANGE REDRAWS THE DASHBOARD (api.js realtime → renderAll). The numbers are fetched again at
// most once a minute; in between the drawn page is left alone.
//
// ⚠️ THE PERIOD MENU NEVER GETS A CSS TRANSFORM: the CRM date picker inside it would measure against the menu.

import { callFunction } from '../api.js';
import { reloadIfStale } from '../stale-build.js';
import { upgradeDates } from '../date-picker.js';
import { escHtml } from '../utils.js';
import { tilesHtml, greeting, DEFS, ICON, info } from './tiles.js';
import { barChart, mixChart, chartWords, bindCharts } from './charts.js';
import { funnelHtml, funnelMenu, layoutFunnel, bindFunnelCard, bindFunnelTips } from './funnel.js';
import { valueRowHtml, ltvChart, cacChart, refundChart, bindValueRow } from './value.js';
import { clientsRowHtml, joinLeftChart, bindClientsRow } from './clients.js';
import { renewChart } from './renewals.js';
import { cashMenu, rangeLabel, plural, dayCount, money, addDay, monthOf, spanWords, longDay } from './words.js';
import { openDrill } from './drill.js';
import { nyShow, nyPaint } from './needs-you.js';
import { nmShow, nmPaint } from './next-move.js';

const FRESH_MS = 60_000;
/** The last answer this tab drew, for an instant redraw on refresh. Per signed-in coach, half an hour at most. */
const LAST = 'revfit_dashboard_last';
const LAST_MS = 30 * 60_000;

/** @type {{ cash: string, from: string, to: string, fl: string, flFrom: string, flTo: string }} */
const state = { cash: 'mtd', from: '', to: '', fl: 'mtd', flFrom: '', flTo: '' };
/** Which From lead to client step is open: kept across redraws. @type {Record<string, boolean>} */
const flOpen = {};
/** @type {any} */
let data = null;
/** @type {Map<string, { body: any, at: number }>} */
const answers = new Map();
/** @type {Promise<any>|null} */
let inFlight = null;
/** @type {Promise<void>|null} */
let css = null;
let bound = false;
/** @type {Record<string, any>} */
let runs = {};
/** @type {HTMLElement|null} */
let popFor = null;
/** Redraws the charts when the page's width changes (the sidebar folding, the window, the page coming into view). */
/** @type {ResizeObserver|null} */
let sizeWatch = null;

/** Who is signed in, from the session supabase-js keeps (sb-<project>-auth-token). Null when unknown. */
function signedIn() {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !/^sb-.+-auth-token$/.test(k)) continue;
      const v = JSON.parse(localStorage.getItem(k) || 'null');
      const id = v && ((v.user && v.user.id) || (v.currentSession && v.currentSession.user && v.currentSession.user.id));
      if (id) return String(id);
    }
  } catch { /* private window */ }
  return null;
}
/** Keep the page's first answer for this tab's next refresh. ⚠️ Only for the coach who saw it. @param {string} key @param {any} body */
function keepLast(key, body) {
  try { const u = signedIn(); if (u) sessionStorage.setItem(LAST, JSON.stringify({ u, key, at: Date.now(), body })); } catch { /* full or private */ }
}
/** This tab's last answer for these choices, if this coach saw it lately. @param {string} key */
function lastAnswer(key) {
  try {
    const v = JSON.parse(sessionStorage.getItem(LAST) || 'null'), u = signedIn();
    if (v && u && v.u === u && v.key === key && Date.now() - v.at < LAST_MS && v.body) return v.body;
  } catch { /* private window */ }
  return null;
}

function ensureCss() {
  // index.html links the stylesheet with the page (#dv-css), so it is there from the first paint
  if (!css && document.getElementById('dv-css')) css = Promise.resolve();
  if (css) return css;
  css = new Promise((resolve) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = `/v/${/** @type {any} */ (window).BUILD || 'dev'}/coach-dashboard.css`;
    l.onload = l.onerror = () => resolve(undefined);
    setTimeout(() => resolve(undefined), 2000); // a stalled stylesheet never holds the page back
    document.head.appendChild(l);
  });
  return css;
}

const query = () => ({
  ...(state.cash === 'custom' ? { cash: 'custom', from: state.from, to: state.to } : { cash: state.cash }),
  ...(state.fl === 'custom' ? { fl: 'custom', flFrom: state.flFrom, flTo: state.flTo } : { fl: state.fl }),
});

/** The numbers for the current choices, from memory when fresh. */
async function fetchNumbers() {
  const q = query(), key = JSON.stringify(q), kept = answers.get(key);
  if (kept && Date.now() - kept.at < FRESH_MS) return kept.body;
  const body = await callFunction('coach-dashboard', null, 'GET', q);
  answers.set(key, { body, at: Date.now() });
  return body;
}

/** @param {HTMLElement} view */
function rootOf(view) {
  let r = /** @type {HTMLElement|null} */ (view.querySelector(':scope > #dv-root'));
  if (!r) {
    r = document.createElement('div');
    r.id = 'dv-root';
    r.className = 'dv dv-quiet-titles';
    view.prepend(r);
  }
  ensureFloating();
  bindOnce();
  return r;
}

/** The loading frame, in the page's shape. ⚠️ index.html carries the same markup inside #view-dashboard, so the first
 * paint is this frame (a test compares the two). ⚠️ IT IS ALSO THE PAGE'S FRAME (10/16, the Today card): the numbers
 * are drawn into .tdy-main and the day into .tdy-side, each on its own clock, so a period pick or a minute's tick never
 * redraws the other. */
export const LOADING = '<div class="dv-wrap has-col" aria-busy="true"><div class="view-header"><div><h1>Dashboard</h1><div class="dv-date">&nbsp;</div></div></div>'
  + '<div class="tdy-split"><div class="tdy-main">'
  + `<div class="dv-grid dv-kpis">${'<section class="dv-card dv-kpi dv-s4 dv-skel"></section>'.repeat(3)}</div>`
  + `<div class="dv-grid dv-charts">${'<section class="dv-card dv-chart dv-s6 dv-skel dv-skel--chart"></section>'.repeat(2)}</div>`
  + '</div><aside class="tdy-side" aria-label="Today"><section class="dv-card tdy dv-skel"></section></aside></div></div>';

/** The Today card's module, once loaded (owner only: a team member never loads it). @type {any} */
let todayMod = null;
/** @type {Promise<any>|null} */
let todayLoad = null;
/** The dashboard is showing: the Today card starts once and keeps itself current (today.js). */
function startToday() {
  if (/** @type {any} */ (window)._appMember) return;
  if (!todayLoad) todayLoad = import('./today.js').then((m) => (todayMod = m));
  todayLoad.then((m) => m.todayShow()).catch((e) => {
    // ⚠️ Never a shimmer forever (final review 10/16): the next dashboard show downloads it again, a tab left open past
    // a deploy reloads itself, and the column says so. Its button reloads the page: a browser may keep a failed
    // download's failure for the life of the page, so trying the same download again can fail without a word.
    todayLoad = null;
    console.warn('[dashboard] the Today card did not load:', e instanceof Error ? e.message : e);
    void reloadIfStale(e);
    const side = document.querySelector('#dv-root .tdy-side');
    if (!side) return;
    side.innerHTML = '<section class="dv-card tdy"><p class="tdy-note" role="alert"><span>Your day didn\'t load.</span> <button type="button" class="dv-link" data-tdy-reload>Reload the page</button></p></section>';
    side.querySelector('[data-tdy-reload]')?.addEventListener('click', () => location.reload());
  });
}

/** The page's frame, put back if a failed load replaced it; the numbers' column. @param {HTMLElement} r */
function frame(r) {
  let main = /** @type {HTMLElement|null} */ (r.querySelector(':scope > .dv-wrap.has-col > .tdy-split > .tdy-main'));
  if (!main) {
    r.innerHTML = LOADING;
    todayMod?.todayPaint();
    main = /** @type {HTMLElement} */ (r.querySelector(':scope > .dv-wrap.has-col > .tdy-split > .tdy-main'));
  }
  return main;
}

/** @param {HTMLElement} r */
function drawLoading(r) {
  if (r.childElementCount) return;
  r.innerHTML = LOADING;
  todayMod?.todayPaint();
}

/**
 * The two monthly charts' cards; their pictures are drawn to their width by drawCharts. Each sits under its own card
 * above it (Clayton 10/20): clients on the left under Active clients, money on the right under Cash collected and MRR.
 */
function chartsRow() {
  if (!data.months) return '';
  const w = chartWords(data);
  return `<div class="dv-grid dv-charts">
    <section class="dv-card dv-chart dv-s6" aria-labelledby="dv-tc-h">
      <div class="dv-card-head dv-chart-head"><div><h2 class="dv-title" id="dv-tc-h">Active clients by month</h2><div class="dv-chart-sum">${w.mixLine}</div></div>${info('mixChart')}</div>
      <div class="dv-plotbox" data-chart="mix"></div>
      <div class="dv-chart-note"><span class="dv-ckey">${[['inPerson', 'In person'], ['hybrid', 'Hybrid'], ['online', 'Online']].map(([k, l]) => `<span><i class="dv-tsw t-${k}"></i>${l}</span>`).join('')}</span><span class="dv-nowrap">Counted at the end of each month.</span></div>
    </section>
    <section class="dv-card dv-chart dv-s6" aria-labelledby="dv-cc-h">
      <div class="dv-card-head dv-chart-head"><div><h2 class="dv-title" id="dv-cc-h">Cash collected by month</h2><div class="dv-chart-sum">${w.cashSum}</div></div>${info('cashChart')}</div>
      <div class="dv-plotbox" data-chart="cash"></div>
      <div class="dv-chart-note">${w.cashNote}</div>
    </section>
  </div>`;
}

/**
 * Draw each chart to its box's width (no chart library: plain SVG).
 * ⚠️ NEVER WHILE HIDDEN: a box on a page that is not showing has no width, and a chart drawn at the 260px fallback
 * then shows stretched to twice its size (review). It is left as it is, and drawn when the page shows (sizeWatch).
 * @param {HTMLElement} r @param {boolean} [force] redraw even at the same width (new numbers, a theme change)
 */
function drawCharts(r, force) {
  if (!data || !data.months) return;
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  // ⚠️ A chart that fills its card's spare height is drawn LAST: measured before the chart beside it was drawn, it
  // took that chart's room too and left a gap (final review, 10/15). ⚠️ The Clients row has two (part 5): every
  // filling box is emptied first, then each is measured and drawn in page order, Joined and left before the
  // Renewals weeks, which fill whatever height the Clients card gives the row.
  const boxes = /** @type {HTMLElement[]} */ ([...r.querySelectorAll('.dv-plotbox[data-chart]')]);
  /** @param {HTMLElement} box @returns {number} the width to draw at, or 0 to leave it */
  const widthOf = (box) => {
    if (!box.clientWidth) { if (force) delete box.dataset.w; return 0; } // drawn with these numbers once it shows
    const W = Math.max(260, Math.round(box.clientWidth));
    if (!force && box.dataset.w === String(W)) return 0;
    box.dataset.w = String(W);
    return W;
  };
  /** @param {HTMLElement} box @param {number} W */
  const drawOne = (box, W) => {
    const kind = box.dataset.chart;
    if (kind === 'ltv') box.innerHTML = data.value ? ltvChart(data.value.ltvByMonth, W, box.clientHeight) : '';
    else if (kind === 'cac') box.innerHTML = data.ads ? cacChart(data.ads.cacByMonth, W, box.clientHeight, data.ads.adsFrom) : '';
    else if (kind === 'jl') box.innerHTML = data.clients ? joinLeftChart(data.clients.byMonth, data.months, W, box.clientHeight) : '';
    else if (kind === 'renew') box.innerHTML = data.renewals ? renewChart(data.renewals.weeks, W, box.clientHeight) : '';
    else if (kind === 'refunds') box.innerHTML = data.value ? refundChart(data.value.refunds.byMonth, W) : '';
    else if (kind === 'cash') box.innerHTML = barChart(data.months, (data.stillToCome && data.stillToCome.total) || 0, W, dark);
    else if (kind === 'mix') box.innerHTML = mixChart(data.months, W);
    bindCharts(box, $('#dv-tip'), DRILLS[kind || '']);
  };
  boxes.filter((b) => !b.classList.contains('dv-fill')).forEach((box) => { const W = widthOf(box); if (W) drawOne(box, W); });
  // The filling charts: measured empty, so no chart's last drawing decides any of their heights (prototype)
  const fills = boxes.filter((b) => b.classList.contains('dv-fill')).map((box) => /** @type {[HTMLElement, number]} */ ([box, widthOf(box)])).filter(([, W]) => W);
  fills.forEach(([box]) => { box.innerHTML = ''; });
  fills.forEach(([box, W]) => drawOne(box, W));
}

// ── What makes up a number (drill.js): a month's cash, a month end's clients, the running total to a day ──
/** @param {number} i the month's index */
function drillCash(i) {
  const m = data.months[i], last = i === data.months.length - 1;
  openDrill({ what: 'cash', from: `${m.month}-01`, to: m.end }, { eyebrow: 'Cash collected by month', title: `${monthOf(`${m.month}-01`)}${last ? ' so far' : ''}`, total: money(m.cash || 0) });
}
/** @param {number} i the month's index */
function drillActive(i) {
  const m = data.months[i], last = i === data.months.length - 1;
  if (!m.active) return;
  openDrill({ what: 'active', day: m.end }, { eyebrow: 'Active clients by month', title: last ? `${monthOf(`${m.month}-01`)}, today` : `End of ${monthOf(`${m.month}-01`)}`, total: plural(m.active.total, 'client', 'clients') });
}
/** @param {number} i the month's index on the Lifetime value line */
function drillLtv(i) {
  const m = data.value && data.value.ltvByMonth[i], last = data.value && i === data.value.ltvByMonth.length - 1;
  if (!m || m.value == null) return;
  openDrill({ what: 'ltv', day: m.end }, { eyebrow: 'Lifetime value by month', title: `${monthOf(`${m.month}-01`)}${last ? ', worked out today' : ''}`, total: money(m.value) });
}
/** @param {number} i the month's index on Joined and left (the same months as Active clients) */
function drillFlow(i) {
  const f = data.clients && data.clients.byMonth[i], m = data.months[i], last = i === data.months.length - 1;
  if (!f || !m) return;
  openDrill({ what: 'active', day: m.end }, { eyebrow: 'Joined and left by month', title: `${monthOf(`${m.month}-01`)}${last ? ' so far' : ''}`, total: `${f.joined + f.returned} joined, ${f.left + f.paused} left` });
}
/** @param {number} i the month's index on Refunded each month */
function drillRefunds(i) {
  const m = data.value && data.value.refunds.byMonth[i], last = data.value && i === data.value.refunds.byMonth.length - 1;
  if (!m || m.amount == null) return;
  const from = `${m.month}-01`, end = addDay(addDay(from, 31).slice(0, 8) + '01', -1);
  openDrill({ what: 'refunds', from, to: last || end > data.today ? data.today : end }, { eyebrow: 'Refunded each month', title: `${monthOf(from)}${last ? ' so far' : ''}`, total: money(m.amount) });
}
/** @param {number} i the week's index on Renewals */
function drillRenew(i) {
  const w = data.renewals && data.renewals.weeks[i];
  if (!w) return;
  openDrill({ what: 'renewals', from: w.from, to: w.to }, { eyebrow: 'Programs ending', title: spanWords(w.from, w.to, Number(data.today.slice(0, 4))), total: plural(w.count, 'program', 'programs') });
}
/** @param {number} i the month's index on Cost to win a client */
function drillCac(i) {
  const m = data.ads && data.ads.cacByMonth[i];
  if (!m || m.fromAds == null) return;
  openDrill({ what: 'cac', day: m.end }, { eyebrow: 'Cost to win a client', title: `The 90 days to ${longDay(m.end, Number(data.today.slice(0, 4)))}`, total: money(m.fromAds) });
}
/** @param {number} i the month end's index on the MRR line */
function drillMrr(i) {
  const m = data.mrr && data.mrr.byMonth[i], last = data.mrr && i === data.mrr.byMonth.length - 1;
  if (!m || m.value == null) return;
  openDrill({ what: 'mrr', day: m.end }, { eyebrow: 'MRR', title: last ? 'Today' : `End of ${monthOf(m.end)}`, total: `${money(m.value)} a month` });
}
/** Which breakdown each chart's columns open. @type {Record<string, (i: number) => void>} */
const DRILLS = { cash: drillCash, mix: drillActive, ltv: drillLtv, jl: drillFlow, refunds: drillRefunds, renew: drillRenew, cac: drillCac };
/** @param {number} t days into the Cash collected period @param {number} total the running total to that day */
function drillRun(t, total) {
  const C = data.cash, to = addDay(C.from, t - 1);
  openDrill({ what: 'cash', from: C.from, to }, { eyebrow: 'Cash collected', title: spanWords(C.from, to, Number(data.today.slice(0, 4))), total: money(total) });
}

/** Watch the page's width, so the charts follow it. @param {HTMLElement} r */
function watchSizes(r) {
  sizeWatch?.disconnect();
  sizeWatch = null;
  // The numbers' column: the cards follow its width, and the Today card growing never wakes the charts
  const wrap = r.querySelector('.tdy-main') || r.querySelector('.dv-wrap');
  if (!wrap || typeof ResizeObserver === 'undefined') return;
  /** @type {ReturnType<typeof setTimeout>|undefined} */
  let t;
  sizeWatch = new ResizeObserver(() => { clearTimeout(t); t = setTimeout(() => { drawCharts(r); layoutFunnel(/** @type {HTMLElement|null} */ (r.querySelector('.dv-fl'))); }, 100); });
  sizeWatch.observe(wrap);
}

/** @param {HTMLElement} r */
function draw(r) {
  const t = tilesHtml(data);
  runs = t.runs;
  const main = frame(r);
  r.querySelector(':scope > .dv-wrap')?.removeAttribute('aria-busy');
  const date = r.querySelector(':scope > .dv-wrap > .view-header .dv-date');
  if (date) date.innerHTML = greeting(data.coachName, data.tz);
  // ⚠️ Only the numbers' column: the Today card beside it keeps its own drawing and scroll
  main.innerHTML = `
    ${t.html}
    ${chartsRow()}
    ${data.funnel ? `<div class="dv-funnel-row">${funnelHtml({ ...data.funnel, tz: data.tz }, flOpen)}</div>` : ''}
    ${data.value ? valueRowHtml(data.value, data.ads || null, { canConnect: data.adsAvailable === true, tz: data.tz, today: data.today }) : ''}
    ${data.clients && data.renewals ? clientsRowHtml(data) : ''}
    <div class="dv-grid ny-row"></div>
    <div class="dv-grid nm-row"></div>`;
  // The bottom row (Needs you, Waiting on you) keeps its own lists and clock (needs-you.js): drawn again here
  nyPaint();
  // So does the last card, Your next move (next-move.js)
  nmPaint();
  bindRuns(r);
  const mrr = r.querySelector('[data-mini="mrr"]');
  if (mrr) bindCharts(mrr, $('#dv-tip'), drillMrr);
  drawCharts(r, true);
  const fl = /** @type {HTMLElement|null} */ (r.querySelector('.dv-fl'));
  layoutFunnel(fl);
  bindFunnelCard(fl, flOpen);
  bindFunnelTips(fl, $('#dv-tip'));
  bindValueRow(r.querySelector('.dv-value-row'), $('#dv-tip'));
  // A new goal changes the card's numbers: forget the kept answers and ask the server again
  bindClientsRow(r.querySelector('.dv-clients-row'), () => {
    answers.clear();
    try { sessionStorage.removeItem(LAST); } catch { /* private window */ }
    return refresh(/** @type {HTMLElement} */ (r.parentElement));
  });
  watchSizes(r);
}

/** A first load that failed: says so, with a way to try again. @param {HTMLElement} view @param {HTMLElement} r @param {unknown} err */
function drawFailed(view, r, err) {
  // A team member: the dashboard is the business's numbers, the owner's only (the server refuses with 403)
  const ownerOnly = !!err && /** @type {any} */ (err).status === 403;
  r.innerHTML = `<div class="dv-wrap"><div class="view-header"><div><h1>Dashboard</h1></div></div>
    <section class="dv-card dv-failed"${ownerOnly ? '' : ' role="alert"'}>${ownerOnly
      ? '<p>The dashboard shows the business\'s numbers, so only the account owner sees it.</p>'
      : `<p>Your dashboard didn't load${err instanceof Error && err.message ? `: ${escHtml(err.message)}` : '.'}</p>
    <button type="button" class="btn-primary" data-dv-retry>Try again</button>`}</section></div>`;
  r.querySelector('[data-dv-retry]')?.addEventListener('click', () => {
    if (inFlight) return;
    r.innerHTML = '';
    drawLoading(r);
    void refresh(view);
  });
}

/**
 * Fetch and draw. ⚠️ A refresh draws this tab's last answer straight away, then the server's (only when it differs).
 * Once numbers are on screen, a failed fetch keeps them.
 * @param {HTMLElement} view @returns {Promise<boolean>} whether the server's answer was drawn
 */
async function refresh(view) {
  const r = rootOf(view);
  const asked = JSON.stringify(query());
  const cssReady = ensureCss();
  // ⚠️ Started before any await: a renderAll while the last answer is drawn finds it and never starts a second
  inFlight = fetchNumbers();
  inFlight.catch(() => {}); // a failure is handled below; this keeps it from counting as unhandled while the draw waits
  try {
    if (!data) {
      const last = lastAnswer(asked);
      if (last) { data = last; await cssReady; draw(r); }
      else drawLoading(r);
    }
    const body = await inFlight;
    if (JSON.stringify(query()) !== asked) return false; // a period picked meanwhile draws its own answer
    const same = !!data && JSON.stringify(body) === JSON.stringify(data) && !!r.querySelector('.dv-kpi:not(.dv-skel)');
    data = body;
    keepLast(asked, body);
    await cssReady;
    if (!same) draw(r);
    return !same;
  } catch (err) {
    if (data) return false;
    console.warn('[dashboard] the dashboard did not load:', err instanceof Error ? err.message : err);
    await cssReady;
    drawFailed(view, r, err);
    return false;
  } finally {
    inFlight = null;
  }
}

/**
 * The dashboard view. Leaves a freshly drawn page alone.
 * @param {HTMLElement} view
 */
export function showCoachDashboard(view) {
  startToday(); // before the early returns: the day keeps itself current even when the numbers are fresh
  nyShow(); // so do Needs you and Waiting on you
  nmShow(); // and Your next move
  const kept = answers.get(JSON.stringify(query()));
  if (data && view.querySelector(':scope > #dv-root .dv-kpi:not(.dv-skel)') && kept && Date.now() - kept.at < FRESH_MS) return;
  if (inFlight) return;
  void refresh(view);
}

// ── The floating parts: the running-total hover, the (i) explainer and the period menu, on <body> ──
function ensureFloating() {
  if (document.getElementById('dv-tip')) return;
  const tip = document.createElement('div'); tip.id = 'dv-tip'; tip.className = 'dv-tip'; tip.setAttribute('role', 'status'); tip.setAttribute('aria-live', 'polite');
  const pop = document.createElement('div'); pop.id = 'dv-pop'; pop.className = 'dv-pop'; pop.setAttribute('role', 'dialog'); pop.setAttribute('aria-label', 'What this counts');
  const menu = document.createElement('div'); menu.id = 'dv-menu'; menu.className = 'dv-menu';
  document.body.append(tip, pop, menu);
  menu.addEventListener('focusout', (e) => {
    const to = /** @type {HTMLElement|null} */ (e.relatedTarget);
    if (to && (to.closest('#dv-menu') || to.closest('.dp-pop') || to.closest('.dv-pick'))) return;
    if (to) closeMenu();
  });
}
const $ = (/** @type {string} */ s) => /** @type {HTMLElement} */ (document.querySelector(s));
/** Which menu is open: the Cash collected card's periods, or From lead to client's dates. @type {'cash'|'fl'} */
let menuKey = 'cash';
const MENUS = {
  cash: { p: 'cash', from: 'from', to: 'to', label: 'Choose a period', list: () => cashMenu(data.today), min: () => (data.cash && data.cash.start) || data.today, compared: true },
  fl: { p: 'fl', from: 'flFrom', to: 'flTo', label: 'Choose the dates', list: () => funnelMenu(data.today), min: () => (data.funnel && data.funnel.start) || data.today, compared: false },
};
const menuBtn = () => /** @type {HTMLElement|null} */ (document.querySelector(`#dv-root .dv-pick[data-menu="${menuKey}"]`));

/** @param {HTMLElement} btn */
function placeMenu(btn) {
  const m = $('#dv-menu'), r = btn.getBoundingClientRect(), mw = 280;
  m.style.left = Math.max(12, Math.min(window.innerWidth - mw - 12, r.right - mw)) + 'px';
  const mh = m.offsetHeight;
  m.style.top = (r.bottom + 6 + mh < window.innerHeight ? r.bottom + 6 : Math.max(12, r.top - 6 - mh)) + 'px';
}

/** Last year has nothing in it for a coach whose payments start this year. @param {string} key */
function periodOk(key) {
  const start = data && data.cash && data.cash.start;
  if (menuKey !== 'cash' || key !== 'ly') return true;
  return !!start && start <= `${Number(data.today.slice(0, 4)) - 1}-12-31`;
}

/** @param {HTMLElement} btn */
function openMenu(btn) {
  menuKey = btn.dataset.menu === 'fl' ? 'fl' : 'cash';
  const M = MENUS[menuKey], S = /** @type {any} */ (state);
  const m = $('#dv-menu');
  m.classList.remove('is-custom');
  m.setAttribute('role', 'menu');
  m.setAttribute('aria-label', M.label);
  const year = Number(data.today.slice(0, 4));
  m.innerHTML = M.list().map((o) => {
    if (!o) return '<hr>';
    const ok = periodOk(o[0]), on = S[M.p] === o[0];
    const note = o[0] === 'custom' ? (on ? rangeLabel(S[M.from], S[M.to], year) : ICON.chevR) : !ok ? 'No data yet' : o[2] || '';
    return `<button type="button" class="dv-mi" role="menuitemradio" aria-checked="${on}" data-p="${o[0]}"${ok ? '' : ' disabled'}>${on ? ICON.check : '<i class="ck"></i>'}${o[1]}${note ? `<span>${note}</span>` : ''}</button>`;
  }).join('');
  m.classList.add('on');
  placeMenu(btn);
  btn.setAttribute('aria-expanded', 'true');
  /** @type {HTMLElement|null} */ (m.querySelector('.dv-mi[aria-checked="true"]') || m.querySelector('.dv-mi:not([disabled])'))?.focus();
}

/** Custom dates: the menu becomes two of the CRM's own date boxes, a live note and Show. */
function openCustom() {
  const m = $('#dv-menu'), btn = menuBtn(), M = MENUS[menuKey], S = /** @type {any} */ (state);
  if (!btn) return;
  const max = data.today, min = M.min() > max ? max : M.min();
  const lastMonth1 = addDay(max.slice(0, 8) + '01', -1).slice(0, 8) + '01';
  const from = S[M.p] === 'custom' ? S[M.from] : (lastMonth1 > min ? lastMonth1 : min);
  const to = S[M.p] === 'custom' ? S[M.to] : max;
  m.classList.add('is-custom');
  m.setAttribute('role', 'dialog');
  m.innerHTML = `<div class="dv-cr" role="group" aria-label="Custom dates">
      <button type="button" class="dv-mi dv-cr-back" data-cr="back">${ICON.back}Custom dates</button>
      <div class="dv-cr-f"><label for="dv-cr-from">From</label><input type="date" id="dv-cr-from" min="${min}" max="${max}" value="${from}"></div>
      <div class="dv-cr-f"><label for="dv-cr-to">To</label><input type="date" id="dv-cr-to" min="${min}" max="${max}" value="${to}"></div>
      <p class="dv-cr-hint" id="dv-cr-hint" aria-live="polite"></p>
      <div class="dv-cr-act"><button type="button" class="btn-secondary" data-cr="cancel">Cancel</button><button type="button" class="btn-primary" data-cr="apply">Show</button></div>
    </div>`;
  upgradeDates(m);
  m.querySelectorAll('input[type="date"]').forEach((el) => el.addEventListener('change', customHint));
  customHint();
  placeMenu(btn);
  /** @type {HTMLElement|null} */ (m.querySelector('.dp-trigger') || m.querySelector('#dv-cr-from'))?.focus();
}

function customHint() {
  const a = /** @type {HTMLInputElement} */ ($('#dv-cr-from')).value, b = /** @type {HTMLInputElement} */ ($('#dv-cr-to')).value;
  const hint = $('#dv-cr-hint'), go = /** @type {HTMLButtonElement} */ ($('#dv-menu [data-cr="apply"]'));
  hint.classList.remove('is-error');
  if (!a || !b) { hint.textContent = 'Pick a start and an end date.'; go.disabled = true; return; }
  if (b < a) { hint.innerHTML = `${ICON.warn}<span>The end date is before the start date.</span>`; hint.classList.add('is-error'); go.disabled = true; return; }
  const days = dayCount(a, b);
  hint.textContent = MENUS[menuKey].compared ? `${plural(days, 'day', 'days')}, compared with ${days === 1 ? 'the day' : `the ${days} days`} right before.` : `${plural(days, 'day', 'days')}.`;
  go.disabled = false;
}

/** @param {boolean} [refocus] */
function closeMenu(refocus) {
  const m = document.getElementById('dv-menu');
  if (!m || !m.classList.contains('on')) return;
  m.classList.remove('on', 'is-custom');
  document.querySelectorAll('.dp-pop').forEach((p) => p.remove()); // a calendar left open inside the menu
  const btn = menuBtn();
  if (btn) { btn.setAttribute('aria-expanded', 'false'); if (refocus) btn.focus(); }
}

/** @param {HTMLElement} btn */
function togglePop(btn) {
  if (popFor === btn) { closePop(); return; }
  closePop();
  const def = DEFS[btn.dataset.def || ''];
  if (!def) return;
  const d = def(data), pop = $('#dv-pop');
  // One line for what it is, then labelled rows (tiles.js DEFS): read at a glance, not as a paragraph
  pop.innerHTML = `<h4>${escHtml(d.title)}</h4><p class="dv-pop-lead">${escHtml(d.lead)}</p>`
    + `<dl class="dv-pop-rows">${d.rows.map(([l, v]) => `<div><dt>${escHtml(l)}</dt><dd>${escHtml(v)}</dd></div>`).join('')}</dl>`
    + (d.note ? `<p class="dv-pop-note">${escHtml(d.note)}</p>` : '');
  const r = btn.getBoundingClientRect(), w = 460;
  pop.style.left = Math.min(window.innerWidth - w - 12, Math.max(12, r.right - w + 12)) + 'px';
  pop.classList.add('on');
  // A tall card (Clients) fits the window: under the button, else above it, else as high as it can go and scrolls
  const ph = pop.offsetHeight;
  pop.style.top = (r.bottom + 8 + ph < window.innerHeight ? r.bottom + 8 : r.top - 8 - ph >= 12 ? r.top - 8 - ph : 12) + 'px';
  btn.setAttribute('aria-expanded', 'true');
  popFor = btn;
}
function closePop() {
  document.getElementById('dv-pop')?.classList.remove('on');
  if (popFor) popFor.setAttribute('aria-expanded', 'false');
  popFor = null;
}

/** A new period: keep the page, fetch its numbers, draw. */
async function choose() {
  const view = /** @type {HTMLElement|null} */ (document.getElementById('view-dashboard'));
  if (!view) return;
  const r = rootOf(view);
  r.querySelector('.dv-kpis')?.setAttribute('aria-busy', 'true');
  const asked = JSON.stringify(query());
  try {
    const body = await fetchNumbers();
    // ⚠️ A late answer for a period picked before this one is dropped, never drawn over it (review, 2026-10-14)
    if (JSON.stringify(query()) !== asked) return;
    data = body;
    draw(r);
  } catch (err) {
    r.querySelector('.dv-kpis')?.removeAttribute('aria-busy');
    const { showToast } = await import('../utils.js');
    showToast(`That period didn't load: ${err instanceof Error ? err.message : err}`, 'error');
  }
  menuBtn()?.focus();
}

function bindOnce() {
  if (bound) return;
  bound = true;
  document.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (!t || !t.closest) return;
    const pick = /** @type {HTMLElement|null} */ (t.closest('#dv-root .dv-pick'));
    if (pick) {
      const same = $('#dv-menu').classList.contains('on') && pick.dataset.menu === menuKey;
      closeMenu();
      if (!same) { closePop(); openMenu(pick); }
      return;
    }
    const item = /** @type {HTMLButtonElement|null} */ (t.closest('#dv-menu button[data-p]'));
    if (item) {
      if (item.disabled) return;
      if (item.dataset.p === 'custom') { openCustom(); return; }
      /** @type {any} */ (state)[MENUS[menuKey].p] = item.dataset.p || 'mtd';
      closeMenu();
      void choose();
      return;
    }
    const cr = /** @type {HTMLButtonElement|null} */ (t.closest('#dv-menu [data-cr]'));
    if (cr) {
      if (cr.dataset.cr === 'back') { const b = menuBtn(); if (b) openMenu(b); }
      else if (cr.dataset.cr === 'cancel') closeMenu(true);
      else if (!cr.disabled) {
        const M = MENUS[menuKey], S = /** @type {any} */ (state);
        S[M.from] = /** @type {HTMLInputElement} */ ($('#dv-cr-from')).value;
        S[M.to] = /** @type {HTMLInputElement} */ ($('#dv-cr-to')).value;
        S[M.p] = 'custom';
        closeMenu();
        void choose();
      }
      return;
    }
    if (!t.closest('#dv-menu') && !t.closest('.dp-pop')) closeMenu();
    const inf = /** @type {HTMLElement|null} */ (t.closest('#dv-root .dv-info'));
    if (inf) { togglePop(inf); return; }
    if (!t.closest('#dv-pop')) closePop();
  });
  document.addEventListener('keydown', (e) => {
    const m = document.getElementById('dv-menu');
    if (e.key === 'Escape') closePop();
    if (!m || !m.classList.contains('on')) return;
    const t = /** @type {HTMLElement} */ (e.target);
    if (t && t.closest && t.closest('.dp-pop')) return; // the calendar handles its own keys
    if (e.key === 'Escape') { closeMenu(true); e.preventDefault(); return; }
    if (m.classList.contains('is-custom')) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = /** @type {HTMLElement[]} */ ([...m.querySelectorAll('.dv-mi:not([disabled])')]), i = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
      items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
      e.preventDefault();
    }
  });
  window.addEventListener('resize', () => { closePop(); closeMenu(); document.getElementById('dv-tip')?.classList.remove('on', 'dv-tip--calc'); });
  // The light and dark themes draw this month's bar differently (a gap, or an outline): redraw when it changes.
  new MutationObserver(() => { const r = document.getElementById('dv-root'); if (r && data) drawCharts(r, true); })
    .observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  // ⚠️ The Scribe answer can land after a refresh drew this tab's last numbers, and the server's same numbers then
  // draw nothing: draw again so "Mark it in Scribe" replaces "Mark it in Booked" (modules/nav-access.js fires it).
  document.addEventListener('fitops:scribe-access', () => {
    const v = document.getElementById('view-dashboard');
    if (data && v && v.querySelector(':scope > #dv-root .dv-kpi:not(.dv-skel)')) draw(rootOf(v));
  });
  document.querySelector('.views-container')?.addEventListener('scroll', () => {
    closePop(); closeMenu();
    document.getElementById('dv-tip')?.classList.remove('on', 'dv-tip--calc');
  }, { passive: true });
}

/** Hover and keys on the cash tile's running totals: a dashed guide, a dot on each line, both totals. @param {HTMLElement} r */
function bindRuns(r) {
  r.querySelectorAll('svg.dv-miniplot[data-run]').forEach((el) => {
    const svg = /** @type {SVGSVGElement} */ (/** @type {unknown} */ (el));
    const sp = runs[svg.dataset.run || ''];
    if (!sp) return;
    const guide = /** @type {SVGLineElement} */ (svg.querySelector('.dv-run-guide'));
    const dots = /** @type {SVGCircleElement[]} */ ([...svg.querySelectorAll('.dv-run-dot')]);
    const tip = $('#dv-tip');
    const last = Math.max(...sp.lines.map((/** @type {any} */ L) => L.upTo), 1);
    let at = -1;
    const show = (/** @type {number} */ t) => {
      at = Math.max(1, Math.min(last, t));
      const px = sp.x(at);
      guide.setAttribute('x1', px); guide.setAttribute('x2', px); guide.style.opacity = '1';
      /** @type {string[]} */
      const rows = [];
      sp.lines.forEach((/** @type {any} */ L, /** @type {number} */ i) => {
        const on = at <= L.upTo;
        dots[i].style.opacity = on ? '1' : '0';
        if (on) {
          const v = L.at(at);
          dots[i].setAttribute('cx', px); dots[i].setAttribute('cy', sp.y(v));
          rows.push(`<i class="dv-tip-sw${i ? ' dv-tip-sw--2' : ''}"></i><span>${escHtml(L.when(at))}</span><em>${money(v)}</em>`);
        }
      });
      tip.classList.remove('dv-tip--calc'); // a box's working-out hover left its two-column layout (final review, 10/15)
      tip.innerHTML = `<div class="dv-tip-rows">${rows.join('')}</div>${at <= sp.lines[0].upTo ? '<p class="dv-tip-note">Click to see who</p>' : ''}`;
      const b = svg.getBoundingClientRect(), tx = b.left + (px / sp.w) * b.width;
      tip.style.left = '0px';
      tip.classList.add('on');
      tip.style.left = Math.max(8, Math.min(window.innerWidth - tip.offsetWidth - 8, tx - tip.offsetWidth / 2)) + 'px';
      tip.style.top = Math.max(8, b.top - tip.offsetHeight - 6) + 'px';
    };
    const hide = () => { at = -1; guide.style.opacity = '0'; dots.forEach((d) => { d.style.opacity = '0'; }); tip.classList.remove('on'); };
    svg.addEventListener('mousemove', (e) => { const b = svg.getBoundingClientRect(), px = ((e.clientX - b.left) / b.width) * sp.w; show(Math.round(((px - 3) / (sp.w - 6)) * sp.n)); });
    svg.addEventListener('mouseleave', hide);
    svg.addEventListener('blur', hide);
    // Tab starts on the last day. ⚠️ A click focuses the line too, and jumped it to the last day before the click picked
    // a day, so the first click opened the whole period (10/31): the mouse's own place stands
    let pointing = false;
    svg.addEventListener('pointerdown', () => { pointing = true; });
    svg.addEventListener('blur', () => { pointing = false; });
    svg.addEventListener('focus', () => { if (!pointing) show(last); pointing = false; });
    // ⚠️ A DAY ON THE LINE OPENS THE PAYMENTS UP TO IT (Clayton 10/30): this period's line, the one the number is
    const pick = () => { if (at > 0 && at <= sp.lines[0].upTo) { const t = at; hide(); drillRun(t, sp.lines[0].at(t)); } };
    svg.classList.add('is-drill');
    svg.addEventListener('click', pick);
    svg.addEventListener('keydown', (e) => {
      const step = e.shiftKey ? 7 : Math.max(1, Math.round(sp.n / 120));
      if (e.key === 'ArrowRight') { show((at < 0 ? last : at) + step); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { show((at < 0 ? last : at) - step); e.preventDefault(); }
      else if (e.key === 'Home') { show(1); e.preventDefault(); }
      else if (e.key === 'End') { show(last); e.preventDefault(); }
      else if (e.key === 'Escape') hide();
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); }
    });
  });
}
